/**
 * CommandExecutor - コマンドを実行する
 *
 * パース済みコマンドを受け取り、対応するハンドラを実行して結果を返す
 * 結果はコンソールに構造化ログとして出力される
 */

import type { ParsedCommand, CommandContext, CommandResult } from './types';
import { commandRegistry } from './CommandRegistry';
import logger from '../utils/logger';

/**
 * コマンドを実行
 */
export async function executeCommand(
  command: ParsedCommand,
  context: CommandContext
): Promise<CommandResult> {
  const startTime = Date.now();

  try {
    // ハンドラを取得
    const handler = commandRegistry.get(command.name);

    if (!handler) {
      const result: CommandResult = {
        success: false,
        message: `Unknown command: ${command.name}`,
        error: {
          code: 'UNKNOWN_COMMAND',
          message: `Command "${command.name}" is not registered`,
          suggestion: getSuggestion(command.name)
        }
      };

      logResult(command, result, Date.now() - startTime);
      return result;
    }

    // 必須パラメータのチェック
    const validationError = validateParameters(command, handler.parameters);
    if (validationError) {
      const result: CommandResult = {
        success: false,
        message: validationError.message,
        error: validationError
      };

      logResult(command, result, Date.now() - startTime);
      return result;
    }

    // ハンドラを実行
    const result = await handler.execute(command, context);

    logResult(command, result, Date.now() - startTime);
    return result;

  } catch (error) {
    const result: CommandResult = {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error',
      error: {
        code: 'EXECUTION_ERROR',
        message: error instanceof Error ? error.message : String(error)
      }
    };

    logResult(command, result, Date.now() - startTime);
    return result;
  }
}

/**
 * 必須パラメータのチェック
 */
function validateParameters(
  command: ParsedCommand,
  parameters: { name: string; required: boolean; aliases?: string[] }[]
): { code: string; message: string; suggestion?: string } | null {
  for (const param of parameters) {
    if (!param.required) continue;

    // namedArgsでチェック
    const hasValue = command.namedArgs[param.name] !== undefined ||
      (param.aliases?.some(alias => command.namedArgs[alias] !== undefined));

    if (!hasValue) {
      return {
        code: 'MISSING_PARAMETER',
        message: `Required parameter "${param.name}" is missing`,
        suggestion: `Add ${param.name}:value to your command`
      };
    }
  }

  return null;
}

/**
 * 未知のコマンドに対するサジェスチョンを生成
 */
function getSuggestion(name: string): string | undefined {
  const completions = commandRegistry.getCompletions(name.slice(0, 2));
  if (completions.length > 0) {
    const names = completions.slice(0, 3).map(c => c.name);
    return `Did you mean: ${names.join(', ')}?`;
  }

  const allCommands = commandRegistry.getAll();
  if (allCommands.length > 0) {
    return `Available commands: ${allCommands.map(c => c.name).join(', ')}`;
  }

  return undefined;
}

/**
 * 実行結果をコンソールに出力
 * AIエージェントはread_console_messagesでこのログを取得できる
 */
function logResult(command: ParsedCommand, result: CommandResult, durationMs: number): void {
  const logData = {
    command: command.name,
    args: command.namedArgs,
    flags: Array.from(command.flags),
    success: result.success,
    message: result.message,
    tabId: result.tabId,
    data: result.data,
    error: result.error,
    durationMs
  };

  if (result.success) {
    logger.info('[AgentCommand] Result', logData);
  } else {
    logger.error('[AgentCommand] Error', logData);
  }
}
